import { 
    homeIconPath,
    homeIconPath2,
    solarIconPath,
    gridIconPath,
    hotwaterIconPath,
    chevronRightIcon,
    sidePanelIcon
  } from '../../../components/common/iconPaths'

const wCondition: any = {
    fine: '☀️',
    "partly-cloudy": '🌤️',
    cloudy: '☁️',
    windy: '💨',
    rainy: '🌧',
    fog: '🌫️'
}

function alwaysTwoDecimal(number: number) {
    if(!number) return '0.00'
    return Intl.NumberFormat('en-EN',{ 
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false,
    }).format(number);
}


export default function getDashboardData(data: any, state: any, weather: any) {
    const selectedHome = state?.monitorSelected?.home || 'value1'
    const selectedSolar = state?.monitorSelected?.solar || 'value1'
    const imported = data?.tou?.totalImport || data?.cards?.gridImportTotal?.value || 0
    const exported = data?.tou?.totalExport || data?.cards?.gridExportTotal?.value || 0
    let gridUsageDesc = 'Net grid import'
    const net = imported - exported
    // if(net < 0 ) gridUsageDesc = 'Net grid export'
    return {
        cards: {
            homeUsage: {
                icon: homeIconPath2,
                cardTitle: 'Solar vs grid',
                value: data?.monitor?.home[selectedHome]?.value,
                unit: '%',
                desc: 'From solar system',
                hoverIcon: sidePanelIcon
            },
            solarUtilization: {
                icon: solarIconPath,
                cardTitle: 'Solar utilisation',
                value: data?.monitor?.solar[selectedSolar]?.value,
                unit: '%',
                desc: 'Used at home',
                hoverIcon: sidePanelIcon
            },
            homeUsageTotal: {
                icon: homeIconPath2,
                cardTitle: 'Energy usage',
                value: alwaysTwoDecimal(data?.cards?.homeUsageTotal?.value),
                unit: 'kWh',
                desc: 'Total used',
                hoverIcon: chevronRightIcon
            },
            solarUtilTotal: {
                icon: solarIconPath,
                cardTitle: 'Solar production',
                value: alwaysTwoDecimal(data?.cards?.solarUtilTotal?.value),
                unit: 'kWh',
                desc: 'Total produced',
                hoverIcon: chevronRightIcon
            },
            gridTotal: {
                icon: gridIconPath,
                cardTitle: 'Grid usage',
                imported:  alwaysTwoDecimal(data?.cards?.gridImportTotal?.value),
                exported:  alwaysTwoDecimal(data?.cards?.gridExportTotal?.value),
                unit: 'kWh',
                descImported: 'Total grid imported',
                descExported: 'Total grid exported',
                hoverIcon: chevronRightIcon
            },
            tou: {
                icon: gridIconPath,
                cardTitle: 'Grid usage',
                desc: gridUsageDesc,
                unit: 'kWh',
                netKw: alwaysTwoDecimal(net),
                offPeak: data?.tou?.offPeak,
                shoulder: data?.tou?.shoulder,
                peak: data?.tou?.peak,
                hoverIcon: chevronRightIcon
            }

        },
        monitor: {
            home: {
                color: 'home',
                comments: data?.monitor?.home?.comments || '',
                toolTip: "See how much of your home's energy consumption has come from the solar power you’ve generated or had stored in your smart battery, and how much has come from the grid. The more you can generate and store, the more you can save on your power bill.",
                monitorTitle: "Today's home usage",
                selected: 'value1',
                icon: homeIconPath,               
                cardTitle: 'Solar vs grid',
                value1: {
                    icon: homeIconPath2,
                    value: data?.monitor?.home?.value1?.value,
                    unit: '%',
                    desc: 'From solar & battery',
                    cardDesc: 'From solar system'
                },
                value2: {
                    icon: homeIconPath2,
                    value: data?.monitor?.home?.value2?.value,
                    unit: '%',
                    desc: 'From grid',
                    cardDesc: 'From grid'
                }
                
            },
            solar: {
                color: 'solar',
                comments: data?.monitor?.solar?.comments || '',
                toolTip:'Check out a breakdown of your home’s electricity use. It shows what percentage of your home’s energy use came either directly from solar panels, or stored energy from your battery, as well as any excess energy you have purchased from the grid. The closer to 100% you are with your solar system, the more savings you will make.',
                monitorTitle: "Today's solar usage",
                selected: 'value1',
                icon: solarIconPath,
                cardTitle: 'Solar utilisation',
                value1: {
                    value: data?.monitor?.solar?.value1?.value,
                    unit: '%',
                    desc: 'Used by house & battery',
                    cardDesc: 'Used at home'
                },
                value2: {
                    value: data?.monitor?.solar?.value2?.value || 0,
                    unit: '%',
                    desc: 'Exported to grid',
                    cardDesc: 'Exported to grid'
                }
            },
            battery: {
                capacity: alwaysTwoDecimal(data?.monitor?.battery?.capacity),
                toolTip:"Your battery is self-charging and being optimised behind-the-scenes by our energy experts. If an outage strikes, keep an eye on this to see how much stored energy remains.",
                monitorTitle: 'Current charge level',
                charged: data?.monitor?.battery?.charged,
                unit: '%',
                xAxesLabels: [
                    '12am','1','2','3','4','5','6','7','8','9','10','11','12',
                    '1pm','2 ','3 ','4 ','5 ','6 ','7 ','8 ','9 ','10 ','11 '
                ],
                series: data?.monitor?.battery?.series || new Array(24).fill(0)
            },
            carbon: {
                toolTip:'By using solar energy to power your home, you save CO₂ emissions. This avoids the greenhouse gas emissions that would otherwise be generated from relying solely on the grid.',
                monitorTitle: `Today's carbon savings`,
                value: alwaysTwoDecimal(data?.monitor?.carbon?.value),
                unit: '',
                desc: data?.monitor?.carbon?.desc,
                comments: data?.monitor?.carbon?.comments,
            }
        },
        hotwater: {
            formatter: ((v: any)=>{
                const h = [...data?.hotwater?.hourlyUsage].sort((a:number,b:number)=>b-a)[0]
                return v>=h?`${v} kWh`:v
            }),
            ...data?.hotwater,
            wattage: alwaysTwoDecimal(data?.hotwater?.wattage),
            energySavingMode: {
                status: data?.hotwater?.energySavingStatus || 'Enabled',
                pausedDescription: 'Energy saving mode is paused until tomorrow'
            },
            sendPrompt: true,
            // promptMessage: data?.hotwater?.troubleshooting?.reminder?.note,
    
            card: {
                isAvailable: data?.hotwater?.isAvailable,
                icon: hotwaterIconPath,
                cardTitle: 'Hot water',
                state: data?.hotwater?.stateTitle,
                desc: !data?.hotwater?.isAvailable?'Not available': 
                        `Energy saving mode ${data?.hotwater?.energySavingStatus?.toLowerCase()}`,
                extraIcon: "/images/Power",
                extraName: `${data?.hotwater?.state?.toLowerCase()}.svg`,
                extraColor: data?.hotwater?.state === 'Heating'
                            ? 'sz.colors.green' : 'sz.colors.gray100',
                hoverIcon: sidePanelIcon
                            
            },
            chart: {
                xAxisLabels: [
                    '12am','1','2','3','4','5','6','7','8','9','10','11','12','1pm','2 ','3 ','4 ','5 ','6 ','7 ','8 ','9 ','10 ','11 '
                ],
                series: data?.hotwater?.hourlyUsage,
                color: 'sz.colors.blue'
            },
            usedToday: {
                amount: alwaysTwoDecimal(data?.hotwater?.total),
                unit: 'kWh',
            },
            weeklyAverage: {
                amount: alwaysTwoDecimal(data?.hotwater?.averageDailyUsage),
                unit: 'kWh',
            },
            
        },
        energyFlow: {
            flows: data?.energyFlow?.flows,
            gridImport: data?.energyFlow?.gridImport,
            gridExport: data?.energyFlow?.gridExport,
            batteryUsed: data?.energyFlow?.batteryUsed,
            batteryCharged: data?.energyFlow?.batteryCharged,
            lastUpdate: data?.energyFlow?.lastUpdate,
            operation: data?.energyFlow?.operation,
            temperature: Math.round(weather?.temp) || '',
            condition: wCondition[weather?.condition] || wCondition['cloudy'],
            home: {
                value: alwaysTwoDecimal(data?.energyFlow?.home),
                unit: 'kW'
            },
            solar: {
                value: alwaysTwoDecimal(data?.energyFlow?.solar),
                unit: 'kW'
            },            
            grid: {
                value: alwaysTwoDecimal(data?.energyFlow?.grid),
                unit: 'kW'
            },           
            battery: {
                value: alwaysTwoDecimal(data?.energyFlow?.battery),
                unit: 'kW',
                charged: data?.monitor?.battery?.charged
            }
        },
        tou: data?.tou
    }
}